import { clsx } from '@alexis/helpers/clsx';
import { genzoTrackActionEvent } from '@alexis/helpers/genzo';
import { gtmTrack } from '@alexis/helpers/gtm';
import { translateText } from '@alexis/helpers/translation';
import NewLegView from '@pages/FlightSearchResult/components/NewLegView';
import { MOCK_BAGGAGE_INFO } from '@pages/FlightSearchResult/components/NewTripCard/__mock__';
import { providerCodesForBaggageFilter } from '@pages/FlightSearchResult/constants';
import { useFlightPLS } from '@pages/FlightSearchResult/contexts/FlightPerLegSearchContext/FlightPerLegSearchContext';
import { useFlightSearchResults } from '@pages/FlightSearchResult/contexts/FlightSearchResultsContext';
import useFlightSearchPageParams from '@pages/FlightSearchResult/hooks/useFlightSearchPageParams';
import useFlightSearchSort from '@pages/FlightSearchResult/hooks/useFlightSearchSort';
import { GenzoTrackApplyFilterActionEventType } from '@pages/FlightSearchResult/types';
import uniq from 'lodash/uniq';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import Price from '@components/Price';
import Skeleton from '@components/Skeleton';

import {
  FLIGHT_SCHEDULE_NORMALISATION,
  OFFER_ENGINE,
  TEST_FEATURES_PARAM,
  MOCK_BAGGAGE_FILTER,
} from '@constants/flight';
import { GTM_ID } from '@constants/general';

import { cloudinaryImageTransformations } from '@helpers/imageTransformations';
import { convertFlightSearchResultFarePriceToPrice } from '@helpers/price';
import { hexToRgba } from '@helpers/utils';
import { webEngageTrackFareClick, webEngageTrackPurchase } from '@helpers/webEngage';

import CabinBaggageIcon from '@icons/cabin-baggage-included.svg';
import CheckedBaggageIcon from '@icons/checked-baggage-included.svg';
import CheckIcon from '@icons/tick_small.svg';

import { FlightSearchResultImpressionLogging } from '@hooks/useFlightSearchResultImpressionLogging';
import { useViewportSize } from '@hooks/useViewportSize';

import {
  getCurrencyState,
  getCurrentSiteState,
  getExchangeRateState,
  getLocaleState,
  getPageViewIdState,
  getPriceOptionsState,
  getTranslationsState,
  getUserState,
} from '@redux/selectors';

import type { TripViewModel } from '@wegoTypes/flights/tripViewModel';

import styles from './NewTripCard.module.scss';

export interface NewTripCardProps {
  cardPosition?: 'first' | 'middle' | 'last' | '';
  gtmAuth?: string;
  gtmPreview?: string;
  hasNoTopBorder?: boolean;
  impressionLogging?: FlightSearchResultImpressionLogging;
  isBaggageFilterOn?: boolean;
  isGroupExpanded?: boolean;
  similarOptionsCount?: number;
  isForceLoading?: boolean;
  tripViewModel?: TripViewModel;
  wegoAnalyticsClientId?: string;
  wegoAnalyticsClientSessionId?: string;
  tripCardIndex?: number;
  genzoTrackApplyFilterSearchView: () => void;
  genzoTrackApplyFilterActionEvent: GenzoTrackApplyFilterActionEventType;
  onSimilarOptionsClick?(): void;
  onTripViewModelSelect?(): void;
}

const NewTripCard: React.FC<NewTripCardProps> = ({
  cardPosition,
  gtmAuth,
  gtmPreview,
  hasNoTopBorder,
  impressionLogging,
  isBaggageFilterOn,
  isGroupExpanded,
  similarOptionsCount,
  tripViewModel,
  isForceLoading,
  wegoAnalyticsClientId,
  wegoAnalyticsClientSessionId,
  tripCardIndex,
  genzoTrackApplyFilterSearchView,
  genzoTrackApplyFilterActionEvent,
  onSimilarOptionsClick,
  onTripViewModelSelect,
}) => {
  const currency = useSelector(getCurrencyState);
  const currentSite = useSelector(getCurrentSiteState);
  const exchangeRate = useSelector(getExchangeRateState);
  const locale = useSelector(getLocaleState);
  const priceOptions = useSelector(getPriceOptionsState);
  const translations = useSelector(getTranslationsState);
  const pageViewIdState = useSelector(getPageViewIdState);
  const user = useSelector(getUserState);

  const { sortType, sortOrder } = useFlightSearchSort();

  const {
    previouslySelectedLegIdPLS,
    handlePLSLegSelect,
    isPLSChangeLegLoading,
    isPerLegSearch,
    isPLSDepartLegSelection,
  } = useFlightPLS();

  const isHighlighted = previouslySelectedLegIdPLS === tripViewModel?.legs[0].leg.id;
  const isTripCardLoading = isForceLoading || isPLSChangeLegLoading;

  const { width } = useViewportSize();

  const mainSkeletonWidth = width >= 1280 ? 548 : width >= 1024 ? 440 : 280;
  const dealSkeletonWidth = width >= 1024 ? 168 : 120;

  const { passengerCount } = useFlightSearchPageParams();

  const { search } = useFlightSearchResults();

  const isNormalizedFlight = tripViewModel?.normalizedFlight;

  const [searchParams, setSearchParams] = useSearchParams();

  const testFeatures = useMemo(() => {
    const testFeaturesSearchParam = searchParams.get(TEST_FEATURES_PARAM);

    return testFeaturesSearchParam ? testFeaturesSearchParam.split(',') : [];
  }, [searchParams]);

  const flightScheduleNormalisationIndicatorEnabled = testFeatures.includes(
    FLIGHT_SCHEDULE_NORMALISATION,
  );

  const fareOfferEngineIndicatorEnabled = testFeatures.includes(OFFER_ENGINE);

  const fares = useMemo(() => {
    if (!tripViewModel) {
      return [];
    }

    return tripViewModel.filteredFares
      .map((fareViewModel) => fareViewModel.fare)
      .sort((fareA, fareB) => fareA.price.amount - fareB.price.amount);
  }, [tripViewModel]);

  const highlightTags = uniq(tripViewModel?.tripCardTags);

  const hasBestTag = highlightTags.includes('best');

  const cheapestTag = useMemo(() => {
    if (highlightTags.includes('cheapest')) {
      return translations.cheapest;
    }
    if (highlightTags.includes('cheapest_direct')) {
      return translations.cheapest_direct;
    }
  }, [highlightTags, translations.cheapest, translations.cheapest_direct]);

  const handleOnCardClick = useCallback(
    (tripViewModel: TripViewModel): void => {
      impressionLogging?.handleTripCardClick(tripViewModel);

      if (!tripViewModel.sponsored && search?.id) {
        searchParams.delete('selected_trip_id');
        searchParams.delete('search_id');
        searchParams.append('selected_trip_id', tripViewModel.tripId);
        searchParams.append('search_id', search.id);

        setSearchParams(searchParams, { replace: true });

        onTripViewModelSelect?.();
      }

      if (gtmAuth && gtmPreview) {
        const gtmData = {
          event: 'eventTracked',
          eventAction: 'detail',
          eventCategory: 'flights',
          eventLabel: 'flight-card',
        };

        gtmTrack(GTM_ID, gtmAuth, gtmPreview, gtmData);
      }
    },
    [search?.id, gtmAuth, gtmPreview, searchParams, setSearchParams],
  );

  const handleOnSimilarOptionsClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>): void => {
      e.stopPropagation();

      onSimilarOptionsClick?.();
    },
    [onSimilarOptionsClick],
  );

  const handleInlineSponsoredAdClick = useCallback<() => void>(() => {
    if (!tripViewModel) {
      return;
    }

    window.open(tripViewModel.filteredFares[0]?.fare.handoffUrl, '_blank');

    if (search) {
      const { legs, adultsCount, childrenCount, infantsCount, cabin } = search;

      webEngageTrackFareClick(
        currentSite.countryCode,
        locale,
        adultsCount,
        childrenCount,
        infantsCount,
        cabin,
        legs,
        tripViewModel.legs,
        tripViewModel.tripId,
        tripViewModel.filteredFares.map((filteredFare) => {
          return { ...filteredFare.fare, provider: filteredFare.provider };
        }),
        {
          ...tripViewModel.filteredFares[0]?.fare,
          provider: tripViewModel.filteredFares[0]?.provider,
        },
      );

      webEngageTrackPurchase();
    }
  }, [currentSite.countryCode, locale, search, tripViewModel]);

  const handleTripCardClick = useCallback(() => {
    if (!tripViewModel) {
      return;
    }

    if (isPerLegSearch) {
      if (isPLSDepartLegSelection) {
        handlePLSLegSelect('depart', tripViewModel);
      } else {
        handlePLSLegSelect('return', tripViewModel, search?.id);
        onTripViewModelSelect?.();
      }

      if (wegoAnalyticsClientId && wegoAnalyticsClientSessionId && tripCardIndex !== undefined) {
        let action = '';

        switch (sortType) {
          case 'price': {
            action = 'cheapest';
            break;
          }
          case 'duration': {
            action = 'fastest';
            break;
          }
          case 'leg1_departure_time':
          case 'leg2_departure_time': {
            action = sortOrder === 'asc' ? 'earliest' : 'latest';
            break;
          }
          case 'score': {
            action = tripViewModel.recommendedSortGroup || '';
            break;
          }

          default: {
            action = '';
            break;
          }
        }

        const eventData = {
          category: 'per_leg',
          object: 'select_flight',
          action,
          id: pageViewIdState.home,
          value: `${tripCardIndex + 1}`,
        };
        genzoTrackActionEvent(
          API_BASE_URL,
          wegoAnalyticsClientId,
          wegoAnalyticsClientSessionId,
          user?.userHash,
          eventData,
        );
      }
    } else {
      handleOnCardClick(tripViewModel);
    }
  }, [
    handleOnCardClick,
    handlePLSLegSelect,
    isPLSDepartLegSelection,
    isPerLegSearch,
    onTripViewModelSelect,
    pageViewIdState.home,
    search?.id,
    sortOrder,
    sortType,
    tripCardIndex,
    tripViewModel,
    user?.userHash,
    wegoAnalyticsClientId,
    wegoAnalyticsClientSessionId,
  ]);

  const showBaggageFilter = testFeatures.includes(MOCK_BAGGAGE_FILTER);

  const baggageInfoFares = useMemo(
    () =>
      tripViewModel?.filteredFares.filter((fare) =>
        providerCodesForBaggageFilter.includes(fare.provider.code),
      ),
    [tripViewModel?.filteredFares],
  );

  const hasBaggageInfo = showBaggageFilter && baggageInfoFares?.length;
  const baggageInfoText = `${baggageInfoFares?.length} fare${
    (baggageInfoFares?.length || 0) > 1 ? 's' : ''
  }`;

  if (isTripCardLoading) {
    return (
      <div className={clsx(styles.container, !!cardPosition && styles[cardPosition])}>
        <div
          className={clsx(
            styles.tripDetailsCard,
            !!tripViewModel?.sponsored && styles.isSponsoredAdTripCard,
            styles.loading,
          )}
        >
          <div className={clsx(styles.legContainer, styles.skeletonLegContainer)}>
            <Skeleton width={48} height={48} borderRadius={4} />
            <Skeleton width={120} height={20} borderRadius={4} />
            <Skeleton width={mainSkeletonWidth} height={56} borderRadius={4} />
          </div>
          <div className={clsx(styles.skeletonDeals)}>
            <Skeleton width={90} height={28} borderRadius={4} />
            <Skeleton width={dealSkeletonWidth} height={36} borderRadius={4} />
          </div>
        </div>
      </div>
    );
  }

  if (!tripViewModel) {
    return null;
  }

  if (tripViewModel.isPlaceholder) {
    return <div />;
  }

  return (
    <div
      data-pw='flightSearchResults_tripCard'
      data-testid='trip-card'
      data-trip-id={tripViewModel.tripId}
      className={clsx(
        styles.container,
        !!tripViewModel.sponsored && styles.isSponsoredAd,
        !!cardPosition && styles[cardPosition],
        hasNoTopBorder && styles.hasNoTopBorder,
        flightScheduleNormalisationIndicatorEnabled && isNormalizedFlight && styles.normalized,
        isBaggageFilterOn && styles.isBaggageFilterOn,
        isHighlighted && styles.isPLSHighlighted,
      )}
      onClick={tripViewModel.sponsored ? handleInlineSponsoredAdClick : undefined}
    >
      {/* Section: Trip Card */}
      <div
        data-testid='tripDetailsCard'
        className={clsx(
          styles.tripDetailsCard,
          !!tripViewModel.sponsored && styles.isSponsoredAdTripCard,
          !((similarOptionsCount && similarOptionsCount > 0) || !!isGroupExpanded) &&
            !cardPosition &&
            styles.roundedBorder,
          !!tripViewModel.tripCardTags?.length && styles.hasTags,
          isBaggageFilterOn && styles.hasBaggageInfo,
        )}
        onClick={handleTripCardClick}
      >
        {/* Section: Trip View Model */}
        <div className={styles.legContainer}>
          <NewLegView
            isOfferEngine={
              fareOfferEngineIndicatorEnabled && fares[0]?.legsSourceTypes[0] === 'OFFER_ENGINE'
            }
            isFareCache={
              fareOfferEngineIndicatorEnabled && fares[0]?.legsSourceTypes[0] === 'FARE_CACHE'
            }
            legViewModel={tripViewModel.legs[0]}
            genzoTrackApplyFilterSearchView={genzoTrackApplyFilterSearchView}
            genzoTrackApplyFilterActionEvent={genzoTrackApplyFilterActionEvent}
          />
        </div>

        {isHighlighted && (
          <div className={styles.highlightCheckbox}>
            <CheckIcon />
          </div>
        )}

        {/* Section: Trip deals */}
        <div data-pw='tripCard_fareDetailsContainer' className={styles.deals}>
          <div className={styles.baggageInfo}>
            {isBaggageFilterOn &&
              MOCK_BAGGAGE_INFO.map((baggageInfo) => {
                const { type, pieces, weight } = baggageInfo;

                const Icon = type === 'checked' ? CheckedBaggageIcon : CabinBaggageIcon;
                let text = translations.uta_included;

                if (pieces && weight) {
                  text = translateText(
                    translations.baggage_weight_kg,
                    locale,
                    `${pieces}x${weight}`,
                  );
                } else if (pieces) {
                  text = translateText(translations.baggage_pieces, locale, pieces);
                } else if (weight) {
                  text = translateText(translations.baggage_weight_kg_total, locale, weight);
                }

                return (
                  <div key={type} className={styles.baggageItem}>
                    <Icon />
                    <span className={styles.baggageInfoText}>{text}</span>
                  </div>
                );
              })}
            {hasBaggageInfo ? (
              <div>
                <CheckedBaggageIcon />
                <span className={styles.baggageInfoText}>{baggageInfoText}</span>
              </div>
            ) : null}
          </div>

          <div className={styles.priceAndRoundTripText}>
            {isPerLegSearch && (
              <div className={styles.roundTripText}>{translations.price_roundtrip}</div>
            )}
            {fares.length > 0 && (
              <Price
                dataPw='tripCard_price'
                isInline
                className={styles.price}
                price={convertFlightSearchResultFarePriceToPrice(
                  fares[0].price,
                  passengerCount,
                  priceOptions.showTotalPrice,
                  true,
                )}
                locale={locale}
                currency={currency}
                exchangeRate={exchangeRate}
              />
            )}
          </div>
        </div>

        {/* Section: Tags and ISA */}
        {(tripViewModel.tripCardTags?.length || tripViewModel.sponsored) && (
          <div className={styles.itineraryTags}>
            {hasBestTag && (
              <div className={clsx(styles.tags, styles.best)}>{translations.best_value}</div>
            )}
            {!!cheapestTag && (
              <div className={clsx(styles.tags, styles.cheapest)}>{cheapestTag}</div>
            )}
            {/* Section sponsored card */}
            {tripViewModel.sponsored ? (
              <div
                data-pw='tripCard_sponsoredAdBanner'
                className={clsx(styles.tags, styles.sponsoredAd)}
                style={{
                  color: tripViewModel.sponsored.brandingColor,
                  borderColor: tripViewModel.sponsored.brandingColor,
                  backgroundColor: hexToRgba(tripViewModel.sponsored.brandingColor, 0.2),
                }}
              >
                <img
                  src={cloudinaryImageTransformations(
                    tripViewModel.sponsored.partnerLogoUrl,
                    'w_200',
                    'h_82',
                    'c_fit',
                    'f_auto',
                    'fl_lossy',
                    'q_auto:low',
                  )}
                />
                <div className={styles.brandHeader}>{tripViewModel.sponsored.brandingHeader}</div>

                <div
                  className={styles.point}
                  style={{ backgroundColor: tripViewModel.sponsored.brandingColor }}
                ></div>

                {translations.flights_inline_ads_sponsored_tag}
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Section: Similar Options */}
      {similarOptionsCount && similarOptionsCount > 0 ? (
        <div
          data-pw='tripCard_similarOptionsBtn'
          className={styles.similarOptions}
          onClick={handleOnSimilarOptionsClick}
        >
          {isGroupExpanded
            ? translateText(translations.less_flight_combination, locale, similarOptionsCount)
            : translateText(translations.flight_combinations, locale, similarOptionsCount)}

          <i className={clsx(styles.chevron, !isGroupExpanded && styles.bottom)}></i>
        </div>
      ) : null}
    </div>
  );
};

export default NewTripCard;
